import React from 'react'
import {
  Body1,
  ButtonColorVariant,
  ButtonSize,
  Heading3,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
  ModalFooter,
  ModalHeader,
} from '@gousto-internal/citrus-react'

import type {
  LoggedOutBoxSummaryController,
  LoggedOutBoxSummaryCta,
  LoggedOutBoxSummaryViews,
} from './types'

type LoggedOutBoxSummaryModalProps = {
  controller: LoggedOutBoxSummaryController
}

type FooterCtaProps = {
  cta: LoggedOutBoxSummaryCta
  onClick: () => void
}

const FooterCta = ({ cta, onClick }: FooterCtaProps) => (
  <ModalButton
    aria-busy={cta.isPending}
    colorVariant={ButtonColorVariant.Primary}
    disabled={cta.isDisabled || cta.isPending}
    onClick={onClick}
    size={ButtonSize.Large}
    type="button"
    width="100%"
  >
    {cta.isPending ? `${cta.label}...` : cta.label}
  </ModalButton>
)

const getContent = (
  step: LoggedOutBoxSummaryController['step'],
  views: LoggedOutBoxSummaryViews,
) => {
  switch (step) {
    case 'postcode':
      return views.postcode.postcode
    case 'delivery':
      return [views.delivery.dateLabel, views.delivery.slot?.label].filter(Boolean).join(', ')
    case 'portionSize':
      return (
        views.portionSize.availablePortionSizes.find(({ isSelected }) => isSelected)?.label ??
        `${views.portionSize.selectedPortionSize} people`
      )
    case 'summary':
      return views.summary.totalPrice
  }
}

const getStepCta = ({ actions, step, views }: LoggedOutBoxSummaryController): FooterCtaProps => {
  switch (step) {
    case 'postcode':
      return {
        cta: views.postcode.continueCta,
        onClick: actions.onPostcodeContinue,
      }
    case 'delivery':
      return {
        cta: views.delivery.continueCta,
        onClick: actions.onDeliveryContinue,
      }
    case 'portionSize':
      return {
        cta: views.portionSize.confirmCta,
        onClick: actions.onPortionSizeConfirm,
      }
    case 'summary':
      return {
        cta: views.summary.checkoutCta,
        onClick: actions.onCheckout,
      }
  }
}

export const LoggedOutBoxSummaryModal = ({ controller }: LoggedOutBoxSummaryModalProps) => {
  const { actions, step, views } = controller
  const currentView = views[step]
  const content = getContent(step, views)
  const stepCta = getStepCta(controller)

  return (
    <Modal
      isOpen={controller.isOpen}
      name="logged-out-box-summary-modal"
      onRequestClose={actions.onClose}
      preventScroll
    >
      <ModalHeader>
        <Heading3>{currentView.title}</Heading3>
        <ModalClose onClose={actions.onClose} />
      </ModalHeader>
      <ModalBody>
        <Body1>{content}</Body1>
      </ModalBody>
      <ModalFooter>
        <FooterCta cta={stepCta.cta} onClick={stepCta.onClick} />
      </ModalFooter>
    </Modal>
  )
}
